import {Button, Col, Container, Row} from "react-bootstrap";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {getCourseList} from "../api/CourseApi";
import {hasPermission} from "../api/UserApi";
import {CourseEdit} from "./CourseEdit";
import {toolTipRenderer} from "../utils/gridUtils";
import {deepCopy, getConfLabel, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForField} from "../utils/formUtils";
import {
  CONF_LABEL_KEY_COURSE,
  COURSE_STATUS_DRAFT,
  COURSE_STATUS_NAMES,
  PERMISSION_CONF_ADMIN,
  PERMISSION_COURSE_ADMIN,
  PERMISSION_STAFF
} from "../constants";

// A new (unsaved) course starts out as a draft
const COURSE_TEMPLATE = {
  id: null,
  title: '',
  subtitle: '',
  description: '',
  course_status: COURSE_STATUS_DRAFT,
  image_url: '',
  course_order: 0,
  instructors: [],
  can_edit: true,
};

export function CourseSettingsCourses() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [courseList, setCourseList] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [errors, setErrors] = useState([]);
  const hasFewRows = (courseList || []).length < 12;
  const gridRef = useRef();
  // Course admins create and delete courses - an instructor can only edit the ones they are assigned to,
  // which the server marks on each row as can_edit
  const isCourseAdmin = hasPermission(PERMISSION_STAFF) || hasPermission(PERMISSION_CONF_ADMIN) ||
    hasPermission(PERMISSION_COURSE_ADMIN);
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));

  function loadCourses() {
    getCourseList((code, data, newErrors) => {
      if (code === 200) {
        setCourseList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadCourses();
  }, []);

  function addCourse() {
    setSelectedCourse(deepCopy(COURSE_TEMPLATE));
  }

  function doneEditing() {
    loadCourses();
    setSelectedCourse(null);
  }

  // Column setup
  const colDefs = [
    {field: 'id', cellRenderer: toolTipRenderer, headerName: 'ID', maxWidth: 80, minWidth: 80},
    {field: 'course_order', cellRenderer: toolTipRenderer, headerName: 'Order', maxWidth: 90, minWidth: 90},
    {field: 'title', cellRenderer: toolTipRenderer, headerName: 'Title', flex: 4, minWidth: 150},
    {
      headerName: 'Status', flex: 2, minWidth: 100, cellRenderer: toolTipRenderer,
      valueGetter: params => COURSE_STATUS_NAMES[params.data?.course_status] || params.data?.course_status,
    },
    {
      headerName: 'Prices', flex: 1, minWidth: 90, cellRenderer: toolTipRenderer,
      valueGetter: params => params.data?.price_count || 0,
    },
    {
      headerName: 'Instructors', flex: 3, minWidth: 120, cellRenderer: toolTipRenderer,
      valueGetter: params => (params.data?.instructors || [])
        .map(instructor => `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim()).join(', '),
    },
    {field: 'page_count', cellRenderer: toolTipRenderer, headerName: 'Pages', flex: 1, minWidth: 80},
    {
      field: 'access_count', cellRenderer: toolTipRenderer, headerName: 'Users', flex: 1, minWidth: 80,
      headerTooltip: 'How many users have access',
    },
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  return (
    <div>
      {!selectedCourse &&
        <Container fluid className="usacm-container-wide">
          <Row>
            <Col>
              <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
                <AgGridReact
                  ref={gridRef}
                  rowData={courseList}
                  columnDefs={colDefs}
                  defaultColDef={defaultColDefs}
                  gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
                  domLayout={hasFewRows ? "autoHeight" : "normal"}
                  onRowClicked={params => setSelectedCourse(params.data)} // the whole row opens the course
                  rowStyle={{cursor: 'pointer'}}
                  getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
                />
              </div>
            </Col>
          </Row>

          {getErrorMessageForField(errors, '') &&
            <Row>
              <Col className="text-center mt-3 usacm-error-message">
                {getErrorMessageForField(errors, '')}
              </Col>
            </Row>
          }

          {isCourseAdmin &&
            <Row>
              <Col className="usacm-button-row mt-3">
                <Button type="button" onClick={() => addCourse()}>Add {courseLabel}</Button>
              </Col>
            </Row>
          }
        </Container>
      }

      {selectedCourse &&
        <CourseEdit origCourse={selectedCourse} onClose={doneEditing} onSaved={setSelectedCourse}/>
      }

    </div>
  );
}
